/**
 * Composant réutilisable pour les items de liste (catégories et zones)
 * Harmonise le style visuel et le comportement
 */

import React from 'react';
import { FaEdit, FaTrash } from 'react-icons/fa';
import { CategoryHeader, CategoryName, ActionButtons } from './styles';
import { darkenColor, createFadedGradient, createDepthShadow } from '../../utils/colorUtils';

interface ListItemProps {
  id: string;
  title: string;
  color: string;
  isVisible: boolean;
  createdAt?: string; // Date de création pour affichage
  isExpanded?: boolean;
  isDragging?: boolean;
  isOwner?: boolean;
  onToggleVisibility?: (id: string) => void;
  onToggleExpand?: (id: string) => void;
  onEdit?: (item: any) => void;
  onDelete?: (item: any) => void;
  onClick?: () => void;
  children?: React.ReactNode;
  leftIcon?: React.ReactNode;
  expandable?: boolean;
  editButton?: React.ReactNode;
  deleteButton?: React.ReactNode;
  itemData?: any; // Données originales pour les callbacks
  // Props pour le drag & drop (catégories)
  draggable?: boolean;
  onDragStart?: (e: React.DragEvent) => void;
  onDragOver?: (e: React.DragEvent) => void;
  onDragLeave?: (e: React.DragEvent) => void;
  onDrop?: (e: React.DragEvent) => void;
  onDragEnd?: (e: React.DragEvent) => void;
  onTouchStart?: (e: React.TouchEvent) => void;
  onTouchMove?: (e: React.TouchEvent) => void;
  onTouchEnd?: (e: React.TouchEvent) => void;
  className?: string;
  [key: string]: any; // Pour les autres props comme data-category-id
}

export const ListItem: React.FC<ListItemProps> = ({
  id,
  title,
  color,
  isVisible,
  createdAt,
  isExpanded,
  isDragging,
  isOwner,
  onToggleVisibility,
  onToggleExpand,
  onEdit,
  onDelete,
  onClick,
  children,
  leftIcon,
  expandable,
  editButton,
  deleteButton,
  itemData,
  // Props pour le drag & drop (catégories)
  draggable,
  onDragStart,
  onDragOver,
  onDragLeave,
  onDrop,
  onDragEnd,
  onTouchStart,
  onTouchMove,
  onTouchEnd,
  className,
  ...otherProps
}) => {
  const handleVisibilityToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    onToggleVisibility?.(id);
  };

  const handleExpandToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    onToggleExpand?.(id);
  };

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    onEdit?.(itemData);
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete?.(id);
  };

  return (
    <div
      draggable={draggable}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      onDragEnd={onDragEnd}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      className={className}
      {...otherProps}
      style={{
        cursor: 'pointer',
        background: createFadedGradient(color, isVisible),
        borderLeft: `3px solid ${darkenColor(color)}`,
        border: `1px solid ${isVisible ? darkenColor(color) : 'rgba(90, 90, 90, 0.4)'}`,
        borderRadius: '6px',
        marginBottom: '0',
        transition: 'all 0.2s ease',
        overflow: 'hidden',
        touchAction: 'none',
        userSelect: 'none',
        opacity: isDragging ? 0.7 : 1,
        minHeight: '36px',
        boxShadow: createDepthShadow(isVisible)
      }}
      onClick={onClick}
    >
      <CategoryHeader 
        style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '6px',
          padding: '6px 10px',
          backgroundColor: 'transparent',
          cursor: 'pointer',
          minHeight: '36px'
        }}
      >
        {/* Checkbox de visibilité */}
        {onToggleVisibility && (
          <input
            type="checkbox"
            checked={isVisible}
            onChange={handleVisibilityToggle}
            onClick={(e) => e.stopPropagation()}
            style={{ 
              marginRight: '6px', 
              cursor: 'pointer', 
              flexShrink: 0,
              width: '14px',
              height: '14px'
            }}
          />
        )}
        
        {/* Icône à gauche */}
        {leftIcon && (
          <div style={{ flexShrink: 0 }}>
            {leftIcon}
          </div>
        )}
        
        {/* Conteneur pour titre et description */}
        <div style={{ 
          flex: 1, 
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: '2px',
          overflow: 'hidden'
        }}>
          {/* Date de création sur la première ligne */}
          {createdAt && (
            <div style={{ 
              fontSize: '10px',
              color: isVisible ? 'rgba(255, 255, 255, 0.6)' : 'rgba(255, 255, 255, 0.4)',
              fontWeight: '400',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              lineHeight: '1.2'
            }}>
              {new Date(createdAt).toLocaleDateString('fr-FR', { 
                day: '2-digit', 
                month: '2-digit', 
                year: '2-digit' 
              })}
            </div>
          )}
          
          {/* Titre sur la deuxième ligne */}
          <CategoryName style={{ 
            margin: 0,
            fontSize: '13px',
            fontWeight: '500',
            color: isVisible ? 'white' : 'rgba(255, 255, 255, 0.7)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            lineHeight: '1.2'
          }}>
            {title}
          </CategoryName>
        </div>
        
        {/* Boutons d'action */}
        <ActionButtons style={{ marginLeft: 'auto', flexShrink: 0, gap: '2px' }}>
          {/* Bouton d'expansion */}
          {expandable && onToggleExpand && (
            <div
              onClick={handleExpandToggle}
              style={{
                cursor: 'pointer',
                transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.3s ease',
                color: isVisible ? 'rgba(255, 255, 255, 0.9)' : 'rgba(255, 255, 255, 0.6)',
                padding: '2px',
                borderRadius: '2px',
                fontSize: '10px',
                minWidth: '16px',
                height: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = 'white';
                e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = isVisible ? 'rgba(255, 255, 255, 0.9)' : 'rgba(255, 255, 255, 0.6)';
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              ▼
            </div>
          )}
          
          {/* Bouton d'édition */}
          {isOwner && onEdit && (
            editButton || (
              <div
                onClick={handleEdit}
                style={{
                  background: 'none',
                  border: 'none',
                  color: isVisible ? 'rgba(255, 255, 255, 0.9)' : 'rgba(255, 255, 255, 0.6)',
                  cursor: 'pointer',
                  padding: '2px',
                  borderRadius: '2px',
                  fontSize: '10px',
                  minWidth: '16px',
                  height: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = 'white';
                  e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = isVisible ? 'rgba(255, 255, 255, 0.9)' : 'rgba(255, 255, 255, 0.6)';
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <FaEdit size={10} />
              </div>
            )
          )}
          
          {/* Bouton de suppression */}
          {isOwner && onDelete && (
            deleteButton || (
              <div
                onClick={handleDelete}
                style={{
                  background: 'none',
                  border: 'none',
                  color: isVisible ? 'rgba(255, 200, 200, 0.9)' : 'rgba(255, 150, 150, 0.6)',
                  cursor: 'pointer',
                  padding: '2px',
                  borderRadius: '2px',
                  fontSize: '10px',
                  minWidth: '16px',
                  height: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = 'rgba(255, 100, 100, 1)';
                  e.currentTarget.style.backgroundColor = 'rgba(255, 100, 100, 0.2)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = isVisible ? 'rgba(255, 200, 200, 0.9)' : 'rgba(255, 150, 150, 0.6)';
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <FaTrash size={10} />
              </div>
            )
          )}
        </ActionButtons>
      </CategoryHeader>
      
      {/* Contenu développé */}
      {children && (
        <div style={{
          maxHeight: isExpanded ? 'none' : '0', /* Pas de limite de hauteur quand développé */
          opacity: isExpanded ? '1' : '0',
          overflow: 'hidden',
          transition: 'all 0.3s ease',
          backgroundColor: 'rgba(0, 0, 0, 0.1)'
        }}>
          <div style={{ padding: '0 14px 14px' }}>
            {children}
          </div>
        </div>
      )}
    </div>
  );
};

export default ListItem;
